import React from 'react';
import { useRouter } from 'next/router';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { DisplayFilters } from 'src/utils/filters/display-filters';
import { toggleIngredientsToAvoidFilters } from 'src/utils/filters/selected-filters';
import SearchIcon from '../Icons/Search/SearchIcon';
import StyledFilledSelect from './StyledFilledSelect';
import CloseIcon from '@mui/icons-material/Close';
import useNonNullContext from '../../hooks/useNonNullContext';
import ConfigContext from '../../context/config';
import { createIngredientsToAvoidRequest } from 'src/services/panacea/ingredients-to-avoid-requests';
import IngredientRequestNoResults from './IngredientRequestNoResults';
import logger from 'logger';
import { Noticeable } from '@honeybadger-io/core/build/src/types';

const containsText = (text: string, searchText: string) =>
  text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

interface Props {
  displayFilters: DisplayFilters;
  selectedFilters: DisplayFilters;
  setSelectedFilters: React.Dispatch<React.SetStateAction<DisplayFilters>>;
}

const IngredientFilter: React.FC<Props> = ({
  displayFilters,
  selectedFilters,
  setSelectedFilters,
}) => {
  const theme = useTheme();
  const router = useRouter();
  const cartUuid = router.query.cartId as string | undefined;
  const tenantConfig = useNonNullContext(ConfigContext);

  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );

  const [dropdownIsOpen, setDropdownIsOpen] = React.useState(false);
  const [searchText, setSearchText] = React.useState('');
  const [requestSubmitted, setRequestSubmitted] = React.useState(false);

  const handleRequestIngredient = async () => {
    if (!cartUuid || !searchText.trim()) return;
    try {
      await createIngredientsToAvoidRequest({
        cartUuid,
        searchValue: searchText.trim(),
      });
      setRequestSubmitted(true);
    } catch (err) {
      logger.error({
        msg: 'FailedToCreateIngredientsToAvoidRequest',
        noticable: err as Noticeable,
        extra: {
          context: {
            cartUuid,
          },
        },
      });
    }
  };

  const [allergenDialogIsOpen, setAllergenDialogIsOpen] = React.useState(false);

  const allIngredientsToAvoid = displayFilters.ingredientsToAvoid;
  const ingredientsToDisplay = React.useMemo(
    () =>
      allIngredientsToAvoid?.filter((ingredient) =>
        containsText(ingredient.displayName, searchText),
      ) || [],
    [searchText],
  );

  const noResults =
    searchText.trim().length > 0 && ingredientsToDisplay.length === 0;

  // Reset request state when search text changes
  React.useEffect(() => {
    setRequestSubmitted(false);
  }, [searchText]);

  if (!allIngredientsToAvoid) {
    return null;
  }

  const selectedIngredientsToAvoid = selectedFilters.ingredientsToAvoid || [];

  const openDropdown = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropdownIsOpen(true);
  };
  const closeDropdown = () => {
    setDropdownIsOpen(false);
    setSearchText('');
  };

  const resetSelectedIngredientsToAvoid = () => {
    setSelectedFilters((prevState) => ({
      ...prevState,
      ingredientsToAvoid: [],
    }));
  };

  const toggleSelectedIngredient = (legacyAllergenItemId: number) => {
    setSelectedFilters((prevState) =>
      toggleIngredientsToAvoidFilters({
        displayFilters,
        currentFilters: prevState,
        legacyAllergenItemId,
      }),
    );
  };

  const label = 'Ingredients to avoid';

  const selectValue = selectedIngredientsToAvoid.map(
    (ingredient) => ingredient.legacyAllergenItemId,
  );

  return (
    <React.Fragment>
      <FormControl sx={{ width: 'auto' }}>
        <StyledFilledSelect
          labelId="ingredient-select-label"
          displayEmpty
          id="ingredient-select"
          multiple
          // When on mobile, prevent the Select from opening its Menu and use Drawer instead
          open={!isSmallScreen && dropdownIsOpen}
          onOpen={openDropdown}
          onClose={closeDropdown}
          value={selectValue}
          label={label}
          renderValue={() => {
            if (!selectedIngredientsToAvoid.length) {
              return <>{label}</>;
            }

            if (selectedIngredientsToAvoid.length === 1) {
              return (
                <div css={{ color: theme.palette.primary.contrastText }}>
                  No {selectedIngredientsToAvoid[0].displayName}
                </div>
              );
            }

            return (
              <div css={{ display: 'flex' }}>
                <div
                  css={{
                    color: theme.palette.primary.contrastText,
                    marginRight: '4px',
                  }}
                >
                  No {selectedIngredientsToAvoid[0].displayName}
                </div>
                <div css={{ color: theme.palette.secondary.contrastText }}>
                  + {selectedIngredientsToAvoid.length - 1} more
                </div>
              </div>
            );
          }}
        >
          {tenantConfig.enableAllergenWarning ? (
            <ListSubheader sx={{ padding: '6px' }}>
              <AllergenWarning
                setAllergenDialogIsOpen={setAllergenDialogIsOpen}
                allergenDialogIsOpen={allergenDialogIsOpen}
              />
            </ListSubheader>
          ) : null}
          <ListSubheader sx={{ padding: '0' }}>
            <TextField
              size="small"
              autoFocus
              placeholder="Search.."
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#E5E5E5' },
                  '&:hover fieldset': { borderColor: '#E5E5E5' },
                  '&.Mui-focused fieldset': { borderColor: '#E5E5E5' },
                  '&.Mui-error fieldset': { borderColor: '#E5E5E5' },
                },
              }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') {
                  // Prevents autoselecting item while typing (default Select behavior)
                  e.stopPropagation();
                }
              }}
            />
          </ListSubheader>

          {/* Desktop/tablet dropdown items */}
          <div css={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {ingredientsToDisplay.map((ingredient) => {
              return (
                <MenuItem
                  key={ingredient.legacyAllergenItemId}
                  disableRipple
                  value={ingredient.legacyAllergenItemId}
                  onClick={() =>
                    toggleSelectedIngredient(ingredient.legacyAllergenItemId)
                  }
                  sx={{
                    padding: '0 9px',
                    '&.Mui-selected, &.Mui-selected.Mui-focusVisible': {
                      backgroundColor: 'transparent',
                    },
                    '&.Mui-selected:hover, &.Mui-selected:hover.Mui-selected.Mui-focusVisible':
                      {
                        backgroundColor: theme.palette.action.hover,
                      },
                  }}
                >
                  <Checkbox
                    disableRipple
                    sx={{ paddingLeft: 0 }}
                    checked={selectedIngredientsToAvoid
                      .map(
                        (selectedIngredient) =>
                          selectedIngredient.legacyAllergenItemId,
                      )
                      .includes(ingredient.legacyAllergenItemId)}
                  />
                  <ListItemText
                    primary={ingredient.displayName}
                    slotProps={{ primary: { sx: { fontSize: '0.875rem' } } }}
                  />
                </MenuItem>
              );
            })}
            {noResults && (
              <IngredientRequestNoResults
                searchText={searchText}
                requestSubmitted={requestSubmitted}
                onSubmit={handleRequestIngredient}
              />
            )}
          </div>

          <div css={{ padding: '9px' }}>
            <Button
              variant="outlined"
              onClick={resetSelectedIngredientsToAvoid}
            >
              Reset
            </Button>
          </div>
        </StyledFilledSelect>
      </FormControl>

      {/* Mobile Drawer: acts as the Select menu on small screens */}
      <Drawer
        anchor="bottom"
        open={isSmallScreen && dropdownIsOpen}
        onClose={closeDropdown}
        ModalProps={{ keepMounted: true }}
        css={{
          zIndex: 2000,
        }}
        slotProps={{
          paper: {
            style: { height: 'auto' },
            sx: {
              borderTopLeftRadius: 10,
              borderTopRightRadius: 10,
            },
          },
        }}
      >
        <Box sx={{ p: '16px 24px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
        </Box>
        <Box sx={{ px: 1, py: 0.5 }}>
          {tenantConfig.enableAllergenWarning ? (
            <ListSubheader sx={{ padding: '0 16px 16px' }}>
              <AllergenWarning
                setAllergenDialogIsOpen={setAllergenDialogIsOpen}
                allergenDialogIsOpen={allergenDialogIsOpen}
              />
            </ListSubheader>
          ) : null}
          <ListSubheader sx={{ padding: '0 16px' }}>
            <TextField
              size="small"
              autoFocus
              placeholder="Search.."
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#E5E5E5' },
                  '&:hover fieldset': { borderColor: '#E5E5E5' },
                  '&.Mui-focused fieldset': { borderColor: '#E5E5E5' },
                  '&.Mui-error fieldset': { borderColor: '#E5E5E5' },
                },
              }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') {
                  // Prevents autoselecting item while typing (default Select behavior)
                  e.stopPropagation();
                }
              }}
            />
          </ListSubheader>
          <List disablePadding sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {ingredientsToDisplay.map((ingredient) => {
              const checked = selectedIngredientsToAvoid
                .map(
                  (selectedIngredient) =>
                    selectedIngredient.legacyAllergenItemId,
                )
                .includes(ingredient.legacyAllergenItemId);
              return (
                <ListItemButton
                  key={ingredient.legacyAllergenItemId}
                  onClick={() =>
                    toggleSelectedIngredient(ingredient.legacyAllergenItemId)
                  }
                  dense
                  sx={{ py: 0.5 }}
                >
                  <Checkbox disableRipple edge="start" checked={checked} />
                  <ListItemText
                    primary={ingredient.displayName}
                    slotProps={{ primary: { sx: { fontSize: '0.875rem' } } }}
                  />
                </ListItemButton>
              );
            })}
            {noResults && (
              <IngredientRequestNoResults
                searchText={searchText}
                requestSubmitted={requestSubmitted}
                onSubmit={handleRequestIngredient}
              />
            )}
          </List>
        </Box>
        <Divider />
        <Box
          sx={{
            p: 2,
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Button variant="outlined" onClick={resetSelectedIngredientsToAvoid}>
            Reset
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={closeDropdown}>
              Done
            </Button>
          </Box>
        </Box>
      </Drawer>
    </React.Fragment>
  );
};

const AllergenWarning = (props: {
  allergenDialogIsOpen: boolean;
  setAllergenDialogIsOpen: (isOpen: boolean) => void;
}) => {
  const { allergenDialogIsOpen, setAllergenDialogIsOpen } = props;

  return (
    <>
      <Alert color={'info'} icon={false}>
        Identify meals formulated without an ingredient.
        <br />
        <span
          onClick={() => setAllergenDialogIsOpen(true)}
          css={{ textDecoration: 'underline', cursor: 'pointer' }}
        >
          Learn more
        </span>
      </Alert>
      <Dialog
        open={allergenDialogIsOpen}
        onClose={() => setAllergenDialogIsOpen(false)}
        // This number just needs to be high enough to supersede the mobile drawer.
        sx={{ zIndex: 3000 }}
      >
        <DialogContent>
          <div css={{ marginBottom: '16px' }}>
            <IconButton
              aria-label="close"
              onClick={() => setAllergenDialogIsOpen(false)}
              sx={(theme) => ({
                position: 'absolute',
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
              })}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant={'h6'}>
              We cannot guarantee that any menu item is completely free from
              traces of allergens.
            </Typography>
            All meals are prepared in facilities that also handle milk, eggs,
            fish, shellfish, tree nuts, peanuts, wheat, and soy. Despite strict
            food-safety practices, cross-contact may occur.
          </div>

          <div css={{ textAlign: 'right' }}>
            <Button
              variant={'contained'}
              onClick={() => setAllergenDialogIsOpen(false)}
            >
              Ok
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default IngredientFilter;
